<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.intervention_records', 2),
                        href: meta.links.index,
                    },
                    {
                        name: group.name,
                        href: meta.links.index_group,
                    },
                    {
                        name: $t('messages.student'),
                        href: meta.links.index_student,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <UnsavedChangesFormWarning :is-dirty="form.isDirty" v-if="form.isDirty" />
        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="mt-5 md:mt-0 bg-gray-50">
                    <AppWell>
                        <AppCard>
                            <InertiaLink :href="meta.links.student_show">
                                <img
                                    :src="student.avatar_or_default"
                                    class="mx-auto rounded-full h-32 w-32 border-4 border-white"
                                />
                            </InertiaLink>

                            <div class="bg-white px-4 py-5 sm:px-6">
                                <div class="flex flex-col justify-center items-center">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                                        <InertiaLink :href="meta.links.student_show">
                                            {{ student.name_or_nickname }}
                                        </InertiaLink>
                                    </h3>
                                    <h3 class="leading-6 font-base text-gray-600">
                                        {{ $t('messages.student') }}
                                    </h3>
                                </div>
                            </div>
                        </AppCard>
                    </AppWell>
                </div>

                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="md:grid md:grid-cols-12 md:gap-6">
                        <div class="sm:col-span-3">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.date')" for="date" />
                                <FormInput
                                    required
                                    id="date"
                                    v-model="form.date"
                                    name="date"
                                    type="date"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.date" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-3">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.duration')" for="duration" />
                                <FormInput
                                    required
                                    id="duration"
                                    v-model="form.duration"
                                    name="duration"
                                    type="time"
                                    step="1"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.duration" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-3">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.external_entities', 1)" for="external_entity_id" />
                                <FormSelect
                                    id="external_entity_id"
                                    v-model="form.external_entity_id"
                                    :options="options.external_entities"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.external_entity_id" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-3">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.channel')" for="channel_id" />
                                <FormSelect
                                    required
                                    id="channel_id"
                                    v-model="form.channel_id"
                                    :options="options.channels"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.channel_id" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.participants')" for="participants" />
                                <FormTextarea
                                    id="participants"
                                    v-model="form.participants"
                                    name="participants"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.participants" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.requested_by')" for="requested_by" />
                                <FormInput
                                    id="requested_by"
                                    v-model="form.requested_by"
                                    name="requested_by"
                                    type="text"
                                    max="191"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.requested_by" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.reason')" for="reason" />
                                <FormInput
                                    id="reason"
                                    v-model="form.reason"
                                    name="reason"
                                    type="text"
                                    max="191"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.reason" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.topics_covered')" for="topics_covered" />
                                <FormTextarea
                                    id="topics_covered"
                                    v-model="form.topics_covered"
                                    name="topics_covered"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.topics_covered" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.agreements_purposes')" for="agreements_purposes" />
                                <FormTextarea
                                    id="agreements_purposes"
                                    v-model="form.agreements_purposes"
                                    name="agreements_purposes"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.agreements_purposes" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-8">
                            <div class="mt-1">
                                <FormLabel
                                    :value="$t('messages.next_intervention_date')"
                                    for="next_intervention_date"
                                />
                                <FormInput
                                    id="next_intervention_date"
                                    v-model="form.next_intervention_date"
                                    name="next_intervention_date"
                                    type="date"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.next_intervention_date" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-4">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.time')" for="next_intervention_time" />
                                <FormInput
                                    id="next_intervention_time"
                                    v-model="form.next_intervention_time"
                                    name="next_intervention_time"
                                    type="time"
                                    step="1"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.next_intervention_time" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <InterventionTypeLevel
                                v-model:type-input="form.type"
                                v-model:assessment-level-input="form.assessment_level"
                                v-model:evaluation-of-intervention-input="form.evaluation_of_intervention"
                                :assessments="options.assessments"
                                :types="options.types"
                            />
                        </div>
                    </div>

                    <div class="sm:col-span-6 mt-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $tc('messages.links', 2) }}
                        </label>
                        <div class="mt-1">
                            <LinkBatchCreate v-model="form.links" :links-input="[]" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import LinkBatchCreate from '@/components/pageSpecific/LinkBatchCreate.vue';
import UnsavedChangesFormWarning from '@/components/Forms/UnsavedChangesFormWarning.vue';
import AppCard from '@/Shared/AppCard.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import AppWell from '@/Shared/AppWell.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import InterventionTypeLevel from '@/components/pageSpecific/InterventionTypeLevel.vue';

export default {
    components: {
        PanelPage,
        SubmitFormButton,
        AppWell,
        FormTextarea,
        AppCard,
        UnsavedChangesFormWarning,
        LinkBatchCreate,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        InterventionTypeLevel,
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'options', 'student', 'group'],
    data() {
        return {
            form: this.$inertia.form({
                date: this.meta.defaults.date,
                duration: '00:00:00',
                topics_covered: null,
                agreements_purposes: null,
                evaluation_of_intervention: null,
                channel_id: null,
                external_entity_id: null,
                assessment_level: null,
                type: null,
                participants: this.meta.defaults.participants,
                requested_by: null,
                reason: null,
                next_intervention_date: null,
                next_intervention_time: null,
                links: [],
            }),
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
    },
};
</script>
